/**
 * 메일 보상 키 검증/분류 — 클라(폼 사전검증)와 서버(권위 검증)가 공유하는 순수 함수.
 * 키 공간은 `deliverItems` (services/server/src/utils/delivery.ts) 가 해석하는 토큰과 일치.
 *
 * `GLORY` 는 외부 redemption 자산이지만 **메일에서만** 지급을 허용한다 — 시즌 보상 같은
 * 운영 수동 지급에 다른 경로가 없기 때문. 실제 지급은 `deliverItems` 의 mail-only 옵트인
 * (`MAIL_ONLY_DELIVERY_ASSET_MAP`)이 담당하므로, 여기서 통과시킨 GLORY 도 메일 외
 * 경로(퀘스트·패스·샵·리그)로는 지급되지 않는다.
 *
 * `MAIL_CURRENCY_KEYS` 는 두 역할을 겸한다 — 이 함수의 통화 화이트리스트이자 어드민 메일
 * 폼의 드롭다운 소스(`apps/admin/.../mail/reward-options.ts`). 한때 이 목록은
 * `DELIVERY_ASSET_MAP` 의 진부분집합으로 **일부러** 좁혀져 있었다(드롭다운을 짧게 유지).
 * 그 절충이 폴크방 시즌 보상 지급을 통째로 막으면서 폐기됐다 — 지금은 일반 지급 채널이
 * 해석하는 재화를 메일로도 전부 줄 수 있다. 새 재화를 `DELIVERY_ASSET_MAP` 에 추가하면
 * 여기에도 함께 넣을 것.
 */
export type MailRewardCategory = 'currency' | 'pet' | 'character' | 'skin' | 'petSlot' | 'adRemove';

export const MAIL_CURRENCY_KEYS = [
  'GOLD',
  'RUBY',
  'EMERALD',
  'SAPPHIRE',
  'STAGE_TICKET',
  'LEAGUE_TICKET',
  'VALHALLA_TICKET',
  'DEGEN_TICKET',
  'DEGEN_SILVER_TICKET',
  'DEGEN_GOLDEN_TICKET',
  'FOLKVANGR_TICKET',
  'GOLDEN_BLESSING',
  'THOR_WRATH',
  'BLESSING_OF_FREIA',
  'UPGRADE_RESET_TICKET',
  'QUICK_GAIN_TICKET',
  'BREAKTHROUGH_STONE_PIECE',
  'BREAKTHROUGH_STONE_LOW',
  'BREAKTHROUGH_STONE_MID',
  'BREAKTHROUGH_STONE_HIGH',
  // ⚠️ SKIN_SHARD 는 이 목록에 **반드시** 있어야 한다. 아래 검증은 통화 화이트리스트를
  // 먼저 보고 그 다음 `SKIN_` 접두사 규칙을 보는데, 여기 없으면 조각이 스킨 키로
  // 오분류되어 "skin key must be SKIN_<charId>_<skinId>" 로 거부된다 — 폴크방 시즌
  // 보상표가 조각을 주는데 지급이 통째로 막혔던 실사고다.
  'SKIN_SHARD',
  // 메일 전용 — 다른 지급 채널은 이 키를 해석하지 못한다(파일 상단 주석 참조).
  'GLORY',
] as const;

export type MailValidationOk = {
  ok: true;
  key: string;
  amount: number;
  category: MailRewardCategory;
};
export type MailValidationErr = { ok: false; reason: string };
export type MailRewardValidation = MailValidationOk | MailValidationErr;

const CURRENCY_SET = new Set<string>(MAIL_CURRENCY_KEYS);

export function validateMailReward(rawKey: string, rawAmount: number): MailRewardValidation {
  const key = (rawKey ?? '').trim().toUpperCase();
  if (!key) return { ok: false, reason: 'empty reward key' };

  if (CURRENCY_SET.has(key)) {
    if (!Number.isFinite(rawAmount) || rawAmount <= 0) {
      return { ok: false, reason: `amount must be > 0 for ${key}` };
    }
    return { ok: true, key, amount: Math.floor(rawAmount), category: 'currency' };
  }

  if (key === 'PET_SLOT' || key === 'PET_SLOT_QUEST') {
    if (!Number.isFinite(rawAmount) || rawAmount <= 0) {
      return { ok: false, reason: `amount must be > 0 for ${key}` };
    }
    return { ok: true, key, amount: Math.floor(rawAmount), category: 'petSlot' };
  }

  if (key === 'AD_REMOVE') {
    return { ok: true, key, amount: 1, category: 'adRemove' };
  }

  if (/^PET_\d+$/.test(key)) {
    const id = Number(key.slice(4));
    if (id <= 0) return { ok: false, reason: `invalid pet id in ${key}` };
    return { ok: true, key: `PET_${id}`, amount: 1, category: 'pet' };
  }

  if (/^CHARACTER_\d+$/.test(key)) {
    const id = Number(key.slice(10));
    if (id <= 0) {
      return { ok: false, reason: `invalid character id in ${key}` };
    }
    return { ok: true, key: `CHARACTER_${id}`, amount: 1, category: 'character' };
  }

  if (/^SKIN_\d+_\d+$/.test(key)) {
    const [, charStr, skinStr] = key.split('_');
    return {
      ok: true,
      key: `SKIN_${Number(charStr)}_${Number(skinStr)}`,
      amount: 1,
      category: 'skin',
    };
  }
  if (key.startsWith('SKIN_')) {
    return { ok: false, reason: 'skin key must be SKIN_<charId>_<skinId>' };
  }

  return { ok: false, reason: `unsupported reward key: ${key}` };
}
